import type { Editor } from '@tiptap/core';

import { collapsedTextIndex } from '~/components/document-editor/extensions/page-guide';

/** A selection as the anchor service wants it: the quote and the text either side of it. */
export interface SelectionCandidate {
  quote: string;
  prefix: string;
  suffix: string;
}

/** How much text either side travels with the quote. The server keeps less; more is waste. */
const CONTEXT_CHARS = 60;

/** Index of the first collapsed character at or after an editor position. */
const offsetAt = (positions: number[], pos: number) => {
  let lo = 0;
  let hi = positions.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (positions[mid] < pos) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/**
 * The editor's current selection as a candidate, or null when nothing is selected.
 *
 * The quote is cut from the same collapsed text the highlights are placed with, which
 * mirrors the server's text index character for character. Reading the selection's own
 * text instead broke on every mark boundary: the server writes a space after each text
 * node, so "<strong>$18,270</strong>." is "$18,270 ." there, and a quote without that space
 * was never found.
 */
export const selectionCandidate = (editor: Editor): SelectionCandidate | null => {
  const { from, to, empty } = editor.state.selection;
  if (empty) return null;
  const { text, positions } = collapsedTextIndex(editor.state.doc);
  let start = offsetAt(positions, from);
  let end = offsetAt(positions, to);
  while (start < end && text[start] === ' ') start += 1;
  while (end > start && text[end - 1] === ' ') end -= 1;
  if (start >= end) return null;
  return {
    quote: text.slice(start, end),
    prefix: text.slice(Math.max(0, start - CONTEXT_CHARS), start),
    suffix: text.slice(end, end + CONTEXT_CHARS)
  };
};
